import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from "react-router-dom"; // Need this for navigation
import { useAppId } from "../../context/AppIdContext";
import 'bootstrap/dist/css/bootstrap.min.css';
import Header from '../../component/header';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const PollingLocationDashboard = () => {
    // --- Context & State ---
    const { appId } = useAppId();
    const navigate = useNavigate(); // Initialize useNavigate
    const [loading, setLoading] = useState(true);
    const [pollingData, setPollingData] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [summary, setSummary] = useState({
        totalVoters: 0,
        totalSent: 0,
        totalRemaining: 0,
        totalLocations: 0
    });

    // --- Fetch Data ---
    useEffect(() => {
        if (appId) {
            fetchPollingData();
        }
    }, [appId]);

    const fetchPollingData = async () => {
        setLoading(true);
        try {
            const response = await axios.post(`${BASE_URL}/api/dis_polling_location_wise_slip_send_dash`, {
                app_id: appId
            });

            if (response.data.success && response.data.data && response.data.data.Success === "1") {
                const data = response.data.data.result || [];
                setPollingData(data);
                
                // Calculate summary
                const totalVoters = data.reduce((sum, item) => sum + (item.total_voter || 0), 0);
                const totalSent = data.reduce((sum, item) => sum + (item.send || 0), 0);
                const totalRemaining = data.reduce((sum, item) => sum + (item.remain || 0), 0);
                
                setSummary({
                    totalVoters,
                    totalSent,
                    totalRemaining,
                    totalLocations: data.length
                });
            }
        } catch (error) {
            console.error("Error fetching polling data:", error);
        } finally {
            setLoading(false);
        }
    };

    // Filter data based on search term
    const filteredData = pollingData.filter(item => {
        if (!searchTerm) return true;
        const lowerSearch = searchTerm.toLowerCase();
        return item.eng_polling_location?.toLowerCase().includes(lowerSearch);
    });

    // Calculate sent percentage
    const sentPercentage = summary.totalVoters > 0 
        ? Math.round((summary.totalSent / summary.totalVoters) * 100) 
        : 0;

    if (loading) {
        return (
            <div className="loader-wrapper">
                <div className="spinner"></div>
                <p className="loader-text">Loading Polling Location Data...</p>
                <style jsx>{`
                    .loader-wrapper {
                        display: flex;
                        flex-direction: column;
                        justify-content: center;
                        align-items: center;
                        min-height: 100vh;
                        width: 100%;
                        background: #F9FAFB;
                    }
                    .spinner {
                        width: 50px;
                        height: 50px;
                        border: 4px solid #F3F4F6;
                        border-top: 4px solid #8B5CF6;
                        border-radius: 50%;
                        animation: spin 1s linear infinite;
                        margin-bottom: 16px;
                    }
                    .loader-text {
                        color: #6B7280;
                        font-weight: 500;
                        font-size: 1.1rem;
                    }
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                `}</style>
            </div>
        );
    }

    return (
        <div className="polling-container">
            {/* Header */}
            <div className="dashboard-header-wrapper">
                <Header title={"Polling Location Slip Send Dashboard"} />
            </div>

            <main className="dashboard-main">
                

                {/* Search and Filter */}
                <div className="search-section">
                    <div className="search-wrapper">
                        <svg className="search-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        <input
                            type="text"
                            className="search-input"
                            placeholder="Search by polling location..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <div className="result-count">
                        Showing {filteredData.length} of {pollingData.length} locations
                    </div>
                </div>

                {/* --- NEW: Grid of Location Cards (Matching Image) --- */}
                <div className="dashboard-grid">
                    {filteredData.length > 0 ? (
                        filteredData.map((item, index) => {
                            // Calculate percentage for progress circle safely
                            const rawPercentage = item.total_voter > 0 ? (item.send / item.total_voter) * 100 : 0;
                            const percentage = rawPercentage.toFixed(1); // Keep one decimal like '1.0%'

                            // SVG Circle Math
                            const sqSize = 80; // Size of the circle SVG
                            const strokeWidth = 8;
                            const radius = (sqSize - strokeWidth) / 2;
                            const viewBox = `0 0 ${sqSize} ${sqSize}`;
                            const dashArray = radius * Math.PI * 2;
                            const dashOffset = dashArray - (dashArray * rawPercentage) / 100;

                            return (
                                <div 
                                    key={index} 
                                    className="polling-circle-card"
                                    // --- THE FIX: Now it ONLY passes the polling location ---
                                    onClick={() => navigate('/polling_location_wise_slip_sending_voter', { 
                                        state: { 
                                            polling_location: item.eng_polling_location 
                                        } 
                                    })}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <div className="circle-container">
                                        <svg width={sqSize} height={sqSize} viewBox={viewBox}>
                                            <circle
                                                className="circle-background"
                                                cx={sqSize / 2}
                                                cy={sqSize / 2}
                                                r={radius}
                                                strokeWidth={strokeWidth}
                                            />
                                            <circle
                                                className="circle-progress"
                                                cx={sqSize / 2}
                                                cy={sqSize / 2}
                                                r={radius}
                                                strokeWidth={strokeWidth}
                                                transform={`rotate(-90 ${sqSize / 2} ${sqSize / 2})`}
                                                style={{
                                                    strokeDasharray: dashArray,
                                                    strokeDashoffset: dashOffset
                                                }}
                                            />
                                            <text
                                                className="circle-text"
                                                x="50%"
                                                y="50%"
                                                dy=".3em"
                                                textAnchor="middle"
                                            >
                                                {percentage}%
                                            </text>
                                        </svg>
                                    </div>
                                    <div className="polling-info">
                                        <div className="polling-stats-row">
                                            <span className="stat-label">Voter : <span className="stat-value text-black">{item.total_voter}</span></span>
                                            <span className="stat-label">Send : <span className="stat-value text-black">{item.send}</span></span>
                                        </div>
                                        <p className="polling-address" title={item.eng_polling_location}>
                                            {item.eng_polling_location}
                                        </p>
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        <div className="no-results" style={{ gridColumn: "1 / -1", textAlign: "center", padding: "40px", color: "#6b7280" }}>
                            No polling locations found.
                        </div>
                    )}
                </div>
            </main>

            <style jsx>{`
                /* Header Wrapper */
                .dashboard-header-wrapper {
                    padding: 16px 20px;
                }
                
                @media (min-width: 768px) {
                    .dashboard-header-wrapper {
                        padding: 25px 25px 0 25px;
                    }
                }

                /* Container */
                .polling-container {
                    background: #E5E7EB;
                    min-height: 100vh;
                    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
                    width: 100%;
                    box-sizing: border-box;
                }

                /* Dashboard Main - PADDING AND WIDTH UPDATED HERE */
                .dashboard-main {
                    padding: 12px 16px;
                    max-width: 100%;
                    margin: 0 auto;
                }
                
                @media (min-width: 768px) {
                    .dashboard-main {
                        padding: 16px 24px;
                    }
                }

                /* Summary Section */
                .summary-section {
                    margin-bottom: 28px;
                }

                .summary-cards {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                    gap: 16px;
                    margin-bottom: 24px;
                }

                .summary-card {
                    background: white;
                    border-radius: 20px;
                    padding: 20px;
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
                    transition: all 0.3s ease;
                    border: 1px solid rgba(139, 92, 246, 0.1);
                }

                .summary-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 24px rgba(139, 92, 246, 0.12);
                }

                .card-icon {
                    width: 56px;
                    height: 56px;
                    border-radius: 16px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: white;
                }

                .total-voters .card-icon { background: linear-gradient(135deg, #8B5CF6, #7C3AED); color: white; }
                .sent .card-icon { background: linear-gradient(135deg, #14B8A6, #0D9488); color: white; }
                .remaining .card-icon { background: linear-gradient(135deg, #F59E0B, #D97706); color: white; }
                .locations .card-icon { background: linear-gradient(135deg, #3B82F6, #2563EB); color: white; }

                .card-content { flex: 1; }

                .card-label {
                    font-size: 0.75rem;
                    font-weight: 600;
                    color: #6B7280;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    display: block;
                    margin-bottom: 6px;
                }

                .card-value {
                    font-size: 1.5rem;
                    font-weight: 800;
                    color: #1F2937;
                }

                @media (min-width: 768px) {
                    .card-value { font-size: 1.75rem; }
                }

                /* Progress Section */
                .progress-section {
                    background: white;
                    border-radius: 20px;
                    padding: 20px;
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
                    border: 1px solid rgba(139, 92, 246, 0.1);
                }

                .progress-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 12px;
                }

                .progress-title {
                    font-size: 0.85rem;
                    font-weight: 600;
                    color: #4B5563;
                }

                .progress-percentage {
                    font-size: 1rem;
                    font-weight: 800;
                    color: #8B5CF6;
                }

                .progress-bar-bg {
                    background: #E5E7EB;
                    border-radius: 20px;
                    height: 10px;
                    overflow: hidden;
                }

                .progress-bar-fill {
                    background: linear-gradient(90deg, #8B5CF6, #14B8A6);
                    border-radius: 20px;
                    height: 100%;
                    transition: width 0.5s ease;
                }

                .progress-stats {
                    display: flex;
                    justify-content: space-between;
                    margin-top: 12px;
                    font-size: 0.75rem;
                    color: #6B7280;
                    font-weight: 500;
                }

                /* Search Section */
                .search-section {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    flex-wrap: wrap;
                    gap: 16px;
                    margin-bottom: 24px;
                }

                .search-wrapper {
                    position: relative;
                    flex: 1;
                    max-width: 400px;
                }

                .search-icon {
                    position: absolute;
                    left: 14px;
                    top: 50%;
                    transform: translateY(-50%);
                    width: 18px;
                    height: 18px;
                    color: #9CA3AF;
                }

                .search-input {
                    width: 100%;
                    padding: 12px 16px 12px 42px;
                    border: 1px solid #E5E7EB;
                    border-radius: 14px;
                    font-size: 0.9rem;
                    outline: none;
                    transition: all 0.2s ease;
                    background: white;
                }

                .search-input:focus {
                    border-color: #8B5CF6;
                    box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
                }

                .result-count {
                    font-size: 0.8rem;
                    font-weight: 500;
                    color: #6B7280;
                    background: white;
                    padding: 8px 16px;
                    border-radius: 20px;
                    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
                }

                /* --- NEW DASHBOARD GRID & CARDS (Matching Image) --- */
                .dashboard-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
                    gap: 20px;
                    margin-top: 20px;
                }

                .polling-circle-card {
                    background: #ffffff;
                    border-radius: 12px;
                    padding: 20px;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
                    display: flex;
                    align-items: center; /* Aligns circle and text horizontally */
                    gap: 20px;
                    transition: all 0.3s ease;
                    border: 1px solid #f1f5f9;
                }

                .polling-circle-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 16px -5px rgba(0, 0, 0, 0.08);
                    border-color: #e2e8f0;
                }

                /* Circular Progress Styles */
                .circle-container {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    flex-shrink: 0;
                }

                .circle-background {
                    fill: none;
                    stroke: #f1f5f9;
                }

                .circle-progress {
                    fill: none;
                    stroke: #8B5CF6; /* Reddish color from image */
                    stroke-linecap: round;
                    transition: stroke-dashoffset 0.8s ease;
                }

                .circle-text {
                    font-size: 16px;
                    font-weight: 700;
                    fill: #8B5CF6;
                }

                /* Info Section */
                .polling-info {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    min-width: 0; /* FIXES THE FLEXBOX OVERFLOW BUG */
                }

                .polling-stats-row {
                    display: flex;
                    justify-content: flex-start;
                    gap: 32px;
                    margin-bottom: 8px;
                }

                .stat-label {
                    font-size: 14px;
                    font-weight: 700;
                    color: #1e293b; /* Dark grey for "Voter :" text */
                }

                .stat-value.text-black {
                    color: #000000;
                }

                .polling-address {
                    font-size: 11px;
                    color: #64748b;
                    margin: 0;
                    font-weight: 500;
                    line-height: 1.4;
                    display: -webkit-box;
                    -webkit-line-clamp: 2; /* Limit to 2 lines */
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    text-transform: uppercase;
                    word-break: break-word; /* ENSURES CONTINUOUS STRINGS BREAK */
                }

                /* Mobile Optimization */
                @media (max-width: 640px) {
                    .dashboard-grid { 
                        grid-template-columns: 1fr; 
                    }
                    .search-section {
                        flex-direction: column;
                        align-items: stretch;
                    }
                    .search-wrapper {
                        max-width: 100%;
                    }
                    .result-count {
                        text-align: center;
                    }
                }
            `}</style>
        </div>
    );
};

export default PollingLocationDashboard;